<script setup>
import { ref, computed, watch } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  projets: { type: Array, default: () => [] },
})

const search      = ref('')
const pageSize    = ref(25)
const currentPage = ref(1)
const sortKey     = ref('code')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.projets
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(p =>
      p.code.toLowerCase().includes(q) ||
      p.nom.toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const totalPages = computed(() => Math.ceil(filtered.value.length / pageSize.value))

const paged = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})

const pageNumbers = computed(() => {
  const total = totalPages.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const cur = currentPage.value
  const pages = new Set([1, total, cur])
  if (cur > 1) pages.add(cur - 1)
  if (cur < total) pages.add(cur + 1)
  return [...pages].sort((a, b) => a - b)
})

function onSearch() { currentPage.value = 1 }
function onPageSize() { currentPage.value = 1 }

function formatInterval(debut, fin) {
  if (!debut || !fin) return null
  const fmt = (d) => new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
  return `${fmt(debut)} → ${fmt(fin)}`
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="relative w-80">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
        <input
          v-model="search"
          @input="onSearch"
          type="text"
          placeholder="Rechercher un projet..."
          class="w-full bg-surface-container-highest border-none rounded-md pl-10 pr-4 py-2 text-sm text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>
    </template>

    <div class="p-8">

        <!-- Header -->
        <div class="mb-8 flex items-end justify-between gap-4">
          <div>
            <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase mb-2">Rapports</h1>
            <p class="text-outline text-sm mt-1">Vue d'ensemble des chantiers actifs — sélectionnez un projet ou une estimation.</p>
          </div>
          <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline">Affichage</span>
            <select
              v-model="pageSize"
              @change="onPageSize"
              class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none"
            >
              <option :value="10">10 Projets</option>
              <option :value="25">25 Projets</option>
              <option :value="50">50 Projets</option>
              <option :value="100">100 Projets</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-2xl border border-outline-variant/10">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
                <th @click="toggleSort('code')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-1/5 cursor-pointer select-none hover:text-on-surface transition-colors">
                  <span class="flex items-center gap-1">Projet<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'code' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
                </th>
                <th class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-2/5">Estimations</th>
                <th @click="toggleSort('premier_rapport')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline cursor-pointer select-none hover:text-on-surface transition-colors">
                  <span class="flex items-center gap-1">Période<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'premier_rapport' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr
                v-for="p in paged"
                :key="p.code"
                class="hover:bg-surface-container-highest/20 transition-colors"
              >
                <!-- Code projet -->
                <td class="px-6 py-4 align-top">
                  <a
                    v-if="p.search_url"
                    :href="p.search_url"
                    class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all"
                  >{{ p.code }}</a>
                  <span v-else class="font-mono text-sm font-bold text-primary/50">{{ p.code }}</span>
                  <div v-if="p.nom" class="text-xs text-outline mt-0.5">{{ p.nom }}</div>
                </td>

                <!-- Estimations -->
                <td class="px-6 py-4 align-top">
                  <div v-if="p.estimations.length > 0" class="space-y-1.5">
                    <div v-for="e in p.estimations" :key="e.code">
                      <a
                        :href="e.rapports_url"
                        class="font-bold text-on-surface text-sm uppercase tracking-tight hover:text-primary transition-all"
                      >{{ e.code }}</a>
                    </div>
                  </div>
                  <span v-else class="text-xs text-outline italic">Aucune estimation</span>
                </td>

                <!-- Période -->
                <td class="px-6 py-4 align-top">
                  <!-- Période globale projet -->
                  <div class="mb-1.5">
                    <a
                      v-if="p.search_url"
                      :href="p.search_url"
                      class="text-xs text-outline hover:text-on-surface transition-colors"
                    >{{ formatInterval(p.premier_rapport, p.dernier_rapport) }}</a>
                    <span v-else-if="p.premier_rapport && !p.can_read_all" class="text-xs text-outline italic">Accès limité</span>
                    <span v-else class="text-xs text-outline italic">Aucun rapport</span>
                  </div>
                  <!-- Période par estimation -->
                  <div v-for="e in p.estimations" :key="e.code + '_period'" class="mt-1">
                    <a
                      v-if="e.search_url"
                      :href="e.search_url"
                      class="text-xs text-outline hover:text-on-surface transition-colors"
                    >{{ formatInterval(e.premier_rapport, e.dernier_rapport) }}</a>
                    <span v-else class="text-xs text-outline italic">Aucun rapport</span>
                  </div>
                </td>
              </tr>

              <!-- Empty state -->
              <tr v-if="filtered.length === 0">
                <td colspan="3" class="px-6 py-16 text-center text-outline text-sm">
                  <span class="material-symbols-outlined text-4xl block mb-3 opacity-30">folder_open</span>
                  Aucun projet trouvé{{ search ? ` pour "${search}"` : '' }}.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-4 flex items-center justify-between gap-4">
          <p class="text-xs text-outline font-bold">
            Page {{ currentPage }} / {{ totalPages }}
          </p>
          <div class="flex items-center gap-1">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="px-3 py-2 text-xs font-black uppercase tracking-widest rounded-md border border-outline-variant/20 text-outline hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <span class="material-symbols-outlined text-sm">chevron_left</span>
            </button>
            <template v-for="(page, i) in pageNumbers" :key="page">
              <span v-if="i > 0 && page - pageNumbers[i - 1] > 1" class="px-2 text-outline text-xs">…</span>
              <button
                @click="currentPage = page"
                :class="page === currentPage
                  ? 'bg-primary text-on-primary shadow-md'
                  : 'text-outline hover:bg-surface-container-highest border border-outline-variant/20'"
                class="min-w-[2rem] px-3 py-2 text-xs font-black rounded-md transition-all"
              >{{ page }}</button>
            </template>
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="px-3 py-2 text-xs font-black uppercase tracking-widest rounded-md border border-outline-variant/20 text-outline hover:bg-surface-container-highest disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <span class="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          </div>
        </div>

      <!-- Footer -->
      <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
        Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
      </footer>

    </div>
  </AppLayout>
</template>
